import React from "react";
import { getImageUrl } from "utils/imageHelper";
import { getCountryName, getStateName } from "../../utils/countryData";

export default function ViewCompanyDetails({ data }) {
  if (!data) return null;

  // Helper to safely resolve image paths
  const resolveImage = (imgPath) => {
    if (!imgPath) return null;
    return typeof imgPath === "string" ? getImageUrl(imgPath) : null;
  };

  const companyLogoUrl = resolveImage(data.companyLogo);
  const companyLogoBlackUrl = resolveImage(data.companyLogoBlack);
  const eventBannerUrl = resolveImage(data.eventBanner);
  const sponsorLogos = Array.isArray(data.sponsorImages)
    ? data.sponsorImages
    : [];

  return (
    <div className="flex flex-col gap-6 text-sm text-gray-800">
      {/* SECTION 1: EVENT DETAILS */}
      <ViewSection
        title="Event Details"
        subtitle="Main details about the event."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ViewDisplayBox label="Title" value={data.companyName} />
          <ViewDisplayBox label="Sub Title" value={data.subTitle} />
          <ViewDisplayBox
            label="Event Type"
            value={data.eventType}
            className="uppercase"
          />

          {/* Color Indicators */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Primary Color</span>

            <div className="view-value flex items-center gap-2">
              {data.primaryColor ? (
                <>
                  <span
                    className="view-color-box"
                    style={{ backgroundColor: data.primaryColor }}
                  />
                  <span className="font-mono uppercase">
                    {data.primaryColor}
                  </span>
                </>
              ) : (
                "N/A"
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="view-label">Secondary Color</span>
            <div className="view-value flex items-center gap-2">
              {data.secondaryColor ? (
                <>
                  <span
                    className="view-color-box"
                    style={{ backgroundColor: data.secondaryColor }}
                  />
                  <span className="font-mono uppercase">
                    {data.secondaryColor}
                  </span>
                </>
              ) : (
                "N/A"
              )}
            </div>
          </div>

          {/* HTML Description Box */}
          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">About Event</span>
            <div
              className="view-value"
              dangerouslySetInnerHTML={{ __html: data.aboutEvent || "N/A" }}
            />
          </div>

          {/* Highlights Box */}
          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">Event Highlights</span>
            <div className="view-value">
              {data.eventHighlights?.length ? (
                <ul className="list-inside list-disc space-y-1">
                  {data.eventHighlights.map((highlight, idx) => (
                    <li key={idx}>{highlight}</li>
                  ))}
                </ul>
              ) : (
                <span className="">No highlights added</span>
              )}
            </div>
          </div>
        </div>
      </ViewSection>

      {/* SECTION 2: EVENT MEDIA */}
      <ViewSection
        title="Event Media"
        subtitle="Showing your event on public schedules."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Logo White */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Event Logo White (120x40)</span>
            <ImageContainer
              src={companyLogoUrl}
              alt="Logo White"
              placeholder="No White Logo"
            />
          </div>

          {/* Logo Black */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Event Logo Black (120x40)</span>
            <ImageContainer
              src={companyLogoBlackUrl}
              alt="Logo Black"
              placeholder="No Black Logo"
            />
          </div>

          {/* Banner */}
          <div className="flex flex-col gap-1">
            <span className="view-label">Event Banner (393x219)</span>
            <ImageContainer
              src={eventBannerUrl}
              alt="Banner"
              placeholder="No Event Banner"
              height="h-32"
            />
          </div>

          {/* Sponsors Grid */}
          <div className="mt-2 flex flex-col gap-1 md:col-span-3">
            <span className="view-label">Sponsor Logos (120x40)</span>
            <div className="view-image-div">
              {sponsorLogos.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                  {sponsorLogos.map((img, index) => (
                    <div
                      key={index}
                      className="flex h-20 items-center justify-center rounded-lg border border-gray-200 bg-white p-2 shadow-sm"
                    >
                      <img
                        src={resolveImage(img)}
                        alt={`Sponsor ${index + 1}`}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-16 items-center justify-center text-xs text-gray-400">
                  No Sponsor Logos Uploaded
                </div>
              )}
            </div>
          </div>
        </div>
      </ViewSection>

      {/* SECTION 3: DATE & TIME */}
      <ViewSection
        title="Date & Time"
        subtitle="Configured event schedule and ticket sales window."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ViewDisplayBox
            label="Event Start Date"
            value={formatDateTimeForInput(data.eventStartDate)}
          />
          <ViewDisplayBox
            label="Event End Date"
            value={formatDateTimeForInput(data.eventEndDate)}
          />
          <ViewDisplayBox
            label="Ticket Sales Start Date"
            value={formatDateTimeForInput(data.ticketStartDate)}
          />
          <ViewDisplayBox
            label="Ticket Sales End Date"
            value={formatDateTimeForInput(data.ticketEndDate)}
          />

          {/* Dynamic Timings */}
          {data.eventTimings?.length > 0 && (
            <div className="flex flex-col gap-2 pt-2 md:col-span-2">
              <span className="view-subtitle">Daily Event Timings</span>
              <div>
                {data.eventTimings.map((timing, i) => (
                  <div
                    key={i}
                    className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3"
                  >
                    <ViewDisplayBox
                      label={timing.day}
                      value={timing.date?.split("T")[0]}
                    />
                    {/* <span className="font-semibold text-gray-700">
                      {timing.day} ({timing.date?.split("T")[0]})
                    </span> */}
                    <ViewDisplayBox
                      label="Start Time"
                      value={timing.startTime}
                    />
                    <ViewDisplayBox label="End Time" value={timing.endTime} />
                    {/* <span className="font-medium text-gray-600">
                      Time:{" "}
                      <span className="font-mono text-gray-900">
                        {timing.startTime} - {timing.endTime}
                      </span>
                    </span> */}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ViewSection>

      {/* SECTION 4: VENUE & LOCATION */}
      <ViewSection title="Venue & Location">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ViewDisplayBox
            label="Country"
            value={
              data.companyCountry ? getCountryName(data.companyCountry) : ""
            }
          />
          <ViewDisplayBox
            label="State"
            value={
              data.companyState
                ? getStateName(data.companyCountry, data.companyState)
                : ""
            }
          />
          <ViewDisplayBox label="District" value={data.companyDistrict} />
          <ViewDisplayBox label="Zipcode" value={data.companyPincode} />
          <ViewDisplayBox
            label="Address"
            value={data.companyAddress}
            className="md:col-span-2"
          />
        </div>
      </ViewSection>

      {/* SECTION 5: ORGANIZER DETAILS */}
      <ViewSection title="Organizer Contact">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ViewDisplayBox label="Organizer Email" value={data.companyEmail} />
          <ViewDisplayBox
            label="Organizer Mobile"
            value={`${data.companyMobileCountryCode || "+91"} ${
              data.companyMobile || ""
            }`}
          />
          <ViewDisplayBox label="Website" value={data.companyWebsite} />
          <ViewDisplayBox label="GST Number" value={data.companyGST} />
        </div>
      </ViewSection>

      {/* SECTION 6: ADMIN DETAILS */}
      <ViewSection title="Admin Details">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <ViewDisplayBox label="Admin Name" value={data.adminName} />
          <ViewDisplayBox label="Admin Email" value={data.adminEmail} />
          <ViewDisplayBox
            label="Admin Mobile"
            value={`${data.adminMobileCountryCode || "+91"} ${
              data.adminMobile || ""
            }`}
          />

          {/* Communication Emails */}
          <div className="flex flex-col gap-1 md:col-span-2">
            <span className="view-label">Communication Emails</span>
            <div className="view-value flex flex-wrap">
              {data.communicationEmails?.length ? (
                data.communicationEmails.map((email, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-2.5 py-1  shadow-sm"
                  >
                    {email}
                  </span>
                ))
              ) : (
                <span className="text-xs text-gray-400">
                  No communication emails listed
                </span>
              )}
            </div>
          </div>
        </div>
      </ViewSection>

      {/* SECTION 7: PRICING & OFFERS */}
      <ViewSection title="Pricing & Offers">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ViewDisplayBox label="Currency" value={data.currencyCode} />
          <ViewDisplayBox label="Ticket Price" value={data.price} />
          <ViewDisplayBox label="Tax (%)" value={data.tax} />
        </div>

        {/* Price Tiers */}
        {data.ticketPriceTiers?.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            <span className="view-subtitle">Date-Based Ticket Price Tiers</span>
            <div className="space-y-3">
              {data.ticketPriceTiers.map((tier, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 gap-3 sm:grid-cols-12"
                >
                  {/* Title - Takes 4 Columns */}
                  <div className="sm:col-span-4">
                    <ViewDisplayBox
                      label="Title"
                      value={tier.title || tier.Title}
                    />
                  </div>

                  {/* Price - Compact Column (Takes 2 Columns) */}
                  <div className="sm:col-span-2">
                    <ViewDisplayBox label="Price" value={tier.price} />
                  </div>

                  {/* Start Date - Takes 3 Columns */}
                  <div className="sm:col-span-3">
                    <ViewDisplayBox
                      label="Start Date"
                      value={formatDateTimeForInput(tier.startDate)}
                    />
                  </div>

                  {/* End Date - Takes 3 Columns */}
                  <div className="sm:col-span-3">
                    <ViewDisplayBox
                      label="End Date"
                      value={formatDateTimeForInput(tier.endDate)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quantity Discounts */}
        {data.ticketDiscounts?.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            <span className="view-subtitle">Ticket Quantity Discounts</span>
            <div className="space-y-2">
              {data.ticketDiscounts.map((discount, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 gap-4 md:grid-cols-3"
                >
                  <ViewDisplayBox label="Min Qty" value={discount.minQty} />
                  <ViewDisplayBox label="Max Qty" value={discount.maxQty} />
                  <ViewDisplayBox
                    label="Discount"
                    value={discount.discountPercentage}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </ViewSection>

      {/* SECTION 8: VISITOR APP FEATURES */}
      {/* <ViewSection title="Visitor App Features">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {data.projectModules?.length ? (
            data.projectModules.map((module) => (
              <span
                key={module}
                className="view-value"
              >
                {module}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400">No modules enabled</span>
          )}
        </div>
      </ViewSection> */}

      {/* SECTION 9: STAFF DETAILS */}
      {/* <ViewSection title="Staff Details">
        <div className="space-y-4">
          {data.staffDetails?.map((staff, idx) => (
            <div
              key={idx}
              className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/40 p-4"
            >
              <h5 className="text-xs font-semibold text-gray-700">
                Staff #{idx + 1} Email: {staff.email || "N/A"}
              </h5>
              <div className="overflow-x-auto">
                <table className="min-w-full rounded-lg border border-gray-200 bg-white text-xs">
                  <thead className="bg-gray-100 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Route</th>
                      <th className="px-3 py-2 text-center">Read</th>
                      <th className="px-3 py-2 text-center">Create</th>
                      <th className="px-3 py-2 text-center">Update</th>
                      <th className="px-3 py-2 text-center">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.permissions?.map((perm, pIdx) => (
                      <tr key={pIdx} className="border-t">
                        <td className="px-3 py-1.5 font-medium">{perm.key}</td>
                        <td className="text-center">
                          {perm.can_read ? "✓" : "—"}
                        </td>
                        <td className="text-center">
                          {perm.can_create ? "✓" : "—"}
                        </td>
                        <td className="text-center">
                          {perm.can_update ? "✓" : "—"}
                        </td>
                        <td className="text-center">
                          {perm.can_delete ? "✓" : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </ViewSection> */}
    </div>
  );
}

// Card Wrapper Section Component
function ViewSection({ title, subtitle, children }) {
  return (
    <div className="view-card flex flex-col gap-6 text-sm">
      <div>
        <div className="view-subtitle">{title}</div>
        {/* {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>} */}
      </div>
      <div>{children}</div>
    </div>
  );
}

// Form-Field Mocked Box
function ViewDisplayBox({ label, value, className = "" }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <span className="view-label">{label}</span>
      <div className="view-value">{value || "N/A"}</div>
    </div>
  );
}

// Media Preview Container Component
function ImageContainer({ src, alt, placeholder, height = "h-28" }) {
  return (
    <div className={` ${height} view-image-div`}>
      {src ? (
        <img
          src={src}
          alt={alt}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <span className="text-xs text-gray-400">{placeholder}</span>
      )}
    </div>
  );
}
const formatDateTimeForInput = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  // Extract UTC components directly to match the DB string exactly
  const pad = (num) => String(num).padStart(2, "0");

  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1);
  const day = pad(date.getUTCDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());

  // Returns "YYYY-MM-DDTHH:mm" for <input type="datetime-local" />
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};
